/**
 * Turn FastAPI / fetch error payloads into a human-readable string.
 * Avoids React rendering "[object Object]" when `detail` is an object or array,
 * and never shows API flags like `error: true` or `force=true` to the user.
 */

const FLAG_KEYS = new Set(['error', 'ok', 'success', 'status', 'code']);

function isBlankMessage(text) {
  if (text == null) return true;
  const t = String(text).trim();
  return (
    !t ||
    t === 'true' ||
    t === 'false' ||
    t === '[object Object]' ||
    t === '{"error":true}' ||
    t === '{"error": true}'
  );
}

/** Rewrite internal API copy into something a teacher can act on. */
export function humanizeApiErrorText(text, fallback = 'Request failed') {
  if (typeof text !== 'string') return fallback;
  let t = text.trim();
  if (isBlankMessage(t)) return fallback;

  t = t.replace(/\s*Pass force\s*=\s*true to publish anyway\.?/gi, '').trim();
  t = t.replace(/\s*Pass force=true\.?/gi, '').trim();
  if (isBlankMessage(t)) return fallback;

  const incomplete = t.match(/^(\d+)\s+subject\(s\) are not fully entered yet\.?$/i);
  if (incomplete) {
    const n = Number(incomplete[1]);
    if (n === 1) {
      return '1 subject is not fully entered yet. Enter and save marks for every student in that subject, then publish again.';
    }
    return `${n} subjects are not fully entered yet. Enter and save marks for every student in those subjects, then publish again.`;
  }

  return t;
}

function pickObjectMessage(detail) {
  if (!detail || typeof detail !== 'object') return null;
  const keys = ['msg', 'message', 'detail', 'error_message', 'errorMessage', 'reason', 'title'];
  for (const key of keys) {
    const value = detail[key];
    if (typeof value === 'string' && !isBlankMessage(value)) return value;
    if (value && typeof value === 'object' && value !== detail) {
      const nested = pickObjectMessage(value);
      if (nested) return nested;
    }
  }
  return null;
}

export function formatApiErrorDetail(detail, fallback = 'Request failed') {
  if (detail == null || detail === '') return fallback;
  if (typeof detail === 'boolean' || typeof detail === 'number') {
    return fallback;
  }
  if (typeof detail === 'string') {
    return humanizeApiErrorText(detail, fallback);
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return humanizeApiErrorText(item, '');
        if (item && typeof item === 'object') {
          return pickObjectMessage(item) || (typeof item.msg === 'string' ? item.msg : null);
        }
        return null;
      })
      .filter((part) => part && !isBlankMessage(part));
    if (parts.length) return humanizeApiErrorText(parts.join('; '), fallback);
    return fallback;
  }
  if (typeof detail === 'object') {
    const picked = pickObjectMessage(detail);
    if (picked) return humanizeApiErrorText(picked, fallback);
    if (detail.detail != null && detail.detail !== detail) {
      const nested = formatApiErrorDetail(detail.detail, '');
      if (nested && !isBlankMessage(nested)) return nested;
    }
    const extra = Object.entries(detail).find(
      ([key, value]) => !FLAG_KEYS.has(key) && typeof value === 'string' && !isBlankMessage(value),
    );
    if (extra) return humanizeApiErrorText(extra[1], fallback);
    return fallback;
  }
  return fallback;
}

/** Normalize any thrown value into a safe UI error string. */
export function toErrorMessage(err, fallback = 'Something went wrong') {
  if (err == null) return fallback;
  if (typeof err === 'boolean' || typeof err === 'number') return fallback;
  if (typeof err === 'string') return formatApiErrorDetail(err, fallback);
  if (err instanceof Error) {
    if (err.detail != null) {
      const fromDetail = formatApiErrorDetail(err.detail, '');
      if (fromDetail && !isBlankMessage(fromDetail)) return fromDetail;
    }
    return formatApiErrorDetail(err.message, fallback);
  }
  if (typeof err === 'object') {
    return formatApiErrorDetail(
      err.detail ?? err.message ?? err.msg ?? err.error_message ?? err,
      fallback,
    );
  }
  return fallback;
}
